import {
  HTTP_INTERCEPTORS,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { getLogger } from 'tofe-core';

import { TOFE_SESSION_CONFIG } from '../tofe-session.module';

const logger = getLogger('[Component][BrowserRefInterceptor]');

/**
 * This interceptor is used to add the browserRef header to all requests.
 * browserRef is a unique identifier for the browser session.
 * Both browserId and browserRef are persisted in sessionStorage to maintain
 * consistency across page refreshes within the same tab session.
 */
@Injectable()
export class BrowserRefInterceptor implements HttpInterceptor {
  private readonly config = inject(TOFE_SESSION_CONFIG);
  private readonly BROWSER_ID_KEY = 'tofe_browser_id';
  private readonly BROWSER_REF_KEY = 'tofe_browser_ref';

  private readonly browserId: string;
  private browserRef: string | null = null;

  constructor() {
    this.browserId = this.getOrCreateBrowserId();
    this.browserRef = this.getBrowserRef();
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Check if the request URL contains the specific base path, otherwise ignore.
    if (!request.url.includes(this.config.apiBaseUrl)) {
      return next.handle(request);
    }

    request = this.cloneRequest(request);

    // Continue with the request.
    return next.handle(request).pipe(
      // Intercept the response and check for the X-BrowserRef header.
      tap((event) => {
        if (event instanceof HttpResponse) {
          const xBrowserRefHeader = event.headers.get('X-Browser-Ref');
          if (xBrowserRefHeader) {
            logger.debug('X-Browser-Ref header found:', xBrowserRefHeader);

            // Store the X-BrowserRef header for future requests.
            this.browserRef = xBrowserRefHeader;
            this.setBrowserRef(xBrowserRefHeader);
          }
        }
      }),
    );
  }

  private cloneRequest(request: HttpRequest<unknown>) {
    const setHeaders: { [name: string]: string | string[] } = {
      'X-Browser-Id': this.browserId,
    };
    // If the browserRef is set, add it as a header to the outgoing request.
    if (this.browserRef) {
      logger.debug('Adding X-Browser-Ref header:', this.browserRef);
      setHeaders['X-Browser-Ref'] = this.browserRef;
    }
    return request.clone({ setHeaders, withCredentials: true });
  }

  private getOrCreateBrowserId(): string {
    try {
      const existingId = sessionStorage.getItem(this.BROWSER_ID_KEY);
      if (existingId) {
        logger.debug('Retrieved existing browserId from sessionStorage:', existingId);
        return existingId;
      }

      const newId = this.generateBrowserId();
      sessionStorage.setItem(this.BROWSER_ID_KEY, newId);
      logger.debug('Generated and stored new browserId:', newId);
      return newId;
    } catch (error) {
      logger.error('SessionStorage not available, using in-memory browserId:', error);
      return this.generateBrowserId();
    }
  }

  private generateBrowserId(): string {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
    );
  }

  private getBrowserRef(): string | null {
    try {
      const storedRef = sessionStorage.getItem(this.BROWSER_REF_KEY);
      if (storedRef) {
        logger.debug('Retrieved browserRef from sessionStorage:', storedRef);
      }
      return storedRef;
    } catch (error) {
      logger.error('SessionStorage not available, browserRef will remain in-memory:', error);
      return null;
    }
  }

  private setBrowserRef(browserRef: string): void {
    try {
      sessionStorage.setItem(this.BROWSER_REF_KEY, browserRef);
      logger.debug('Stored browserRef in sessionStorage:', browserRef);
    } catch (error) {
      logger.error('Failed to store browserRef in sessionStorage:', error);
    }
  }
}

export const BrowserRefInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: BrowserRefInterceptor,
  multi: true,
};
